import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import PortalClienteEmbedded from '../../portal/PortalClienteEmbedded';
import { submitPortalAccessRequest } from '../../services/portalAccessRequestService';

const TEST_ACCOUNTS = [
  { label: 'Admin', email: 'admin@gearcontrol.com', password: 'admin123', color: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
  { label: 'Recepción', email: 'recepcion@gearcontrol.com', password: 'recepcion123', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
  { label: 'Taller', email: 'taller@gearcontrol.com', password: 'taller123', color: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
  { label: 'Ventas', email: 'ventas@gearcontrol.com', password: 'ventas123', color: 'bg-green-100 text-green-700 hover:bg-green-200' },
];

const PORTAL_REMEMBERED_EMAIL_KEY = 'portal_remembered_email';
const PORTAL_SESSION_ONLY_KEY = 'portal_session_only';

const SignIn = () => {
  const navigate = useNavigate();
  const authContext = useAuth();
  const { signIn } = authContext || {};

  // Internal login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginDebugState, setLoginDebugState] = useState('');

  // Portal cliente state
  const [showPortalLogin, setShowPortalLogin] = useState(false);
  const [portalUser, setPortalUser] = useState(null);
  const [portalEmail, setPortalEmail] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [showPortalPassword, setShowPortalPassword] = useState(false);

  // Portal checkboxes
  const [rememberSession, setRememberSession] = useState(true);
  const [rememberData, setRememberData] = useState(false);

  // ── Solicitar acceso state ────────────────────────────────────────────────
  const [showRequestAccess, setShowRequestAccess] = useState(false);
  const [requestNombre, setRequestNombre] = useState('');
  const [requestTelefono, setRequestTelefono] = useState('');
  const [requestEmail, setRequestEmail] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestResult, setRequestResult] = useState(null); // { existingClient, requestId }

  // On mount: pre-fill email if previously saved; sign out if session-only flag is set
  useEffect(() => {
    const savedEmail = localStorage.getItem(PORTAL_REMEMBERED_EMAIL_KEY);
    if (savedEmail) {
      setPortalEmail(savedEmail);
      setRememberData(true);
    }

    // If previous login was session-only, clear the Supabase session now
    const sessionOnly = sessionStorage.getItem(PORTAL_SESSION_ONLY_KEY);
    if (sessionOnly === '1') {
      sessionStorage.removeItem(PORTAL_SESSION_ONLY_KEY);
      supabase?.auth?.signOut()?.catch(() => {});
    }
  }, []);

  // ── Internal staff login ──────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoginDebugState('');

    // PASO A — local validation
    if (!email?.trim()) {
      setError('El correo electrónico es requerido.');
      return;
    }
    if (!password) {
      setError('La contraseña es requerida.');
      return;
    }

    setLoading(true);
    setLoginDebugState('START');
    console.log('[SignIn] START');

    try {
      // ── Verify Supabase config ────────────────────────────────────────────
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseKey) {
        setLoginDebugState('AUTH_CONFIG_MISSING');
        console.error('[SignIn] Supabase auth config missing');
        setError('Supabase auth config missing. Verifica las variables de entorno.');
        return;
      }
      console.log('[SignIn] AUTH_CONFIG_OK');

      setLoginDebugState('AUTH_REQUEST');
      console.log('[SignIn] AUTH_REQUEST');
      const authStart = Date.now();

      // Diagnostic timeout — 12s, only for display. Does NOT cancel the real promise.
      let signInTimeoutId = null;
      const timeoutSignal = new Promise((resolve) => {
        signInTimeoutId = setTimeout(() => resolve('__TIMEOUT__'), 12000);
      });

      // Direct call — no wrapper, no race on the call itself
      const authCall = supabase?.auth?.signInWithPassword({
        email: email?.trim(),
        password,
      })?.then((r) => r)?.catch((e) => ({ data: null, error: e }));

      const winner = await Promise.race([authCall, timeoutSignal]);
      clearTimeout(signInTimeoutId);

      const authElapsed = Date.now() - authStart;

      if (winner === '__TIMEOUT__') {
        console.log(`[SignIn] AUTH_REQUEST_TIMEOUT (${authElapsed}ms)`);
        setLoginDebugState(`AUTH_REQUEST_TIMEOUT (${authElapsed}ms): AUTH endpoint no respondió`);
        setError(`AUTH endpoint no respondió en 12s. Verifica tu conexión o el estado de Supabase.`);
        // Let the real call settle in background
        authCall?.then(() => supabase?.auth?.signOut?.()?.catch(() => {}));
        return;
      }

      const { data: authData, error: authError } = winner;

      if (authError) {
        console.log(`[SignIn] AUTH_REQUEST_ERROR (${authElapsed}ms)`, authError?.message);
        setLoginDebugState(`AUTH_REQUEST_ERROR (${authElapsed}ms): ${authError?.message || 'unknown'}`);
        const msg = authError?.message?.toLowerCase();
        if (msg?.includes('invalid login') || msg?.includes('invalid credentials') || msg?.includes('wrong password')) {
          setError('Correo o contraseña incorrectos.');
        } else if (msg?.includes('email not confirmed')) {
          setError('Correo no confirmado. Revisa tu bandeja de entrada.');
        } else if (msg?.includes('too many requests')) {
          setError('Demasiados intentos. Espera unos minutos.');
        } else {
          setError(authError?.message || 'Error al iniciar sesión.');
        }
        return;
      }

      if (!authData?.user) {
        console.log(`[SignIn] AUTH_REQUEST_ERROR (${authElapsed}ms) No user returned`);
        setLoginDebugState(`AUTH_REQUEST_ERROR (${authElapsed}ms): No user returned`);
        setError('No se recibió usuario de Auth.');
        return;
      }

      console.log(`[SignIn] AUTH_REQUEST_SUCCESS (${authElapsed}ms)`, authData?.user?.id);
      setLoginDebugState(`AUTH_REQUEST_SUCCESS (${authElapsed}ms)`);

      // Profile fetch — non-blocking, best-effort
      setLoginDebugState(`AUTH_REQUEST_SUCCESS (${authElapsed}ms) → PROFILE_FETCH_START`);
      console.log('[SignIn] PROFILE_FETCH_START');
      try {
        await supabase?.from('user_profiles')?.select('id, role')?.eq('id', authData?.user?.id)?.maybeSingle();
        console.log('[SignIn] PROFILE_FETCH_OK');
        setLoginDebugState(`AUTH_REQUEST_SUCCESS (${authElapsed}ms) → PROFILE_FETCH_OK`);
      } catch (profileErr) {
        console.warn('[SignIn] PROFILE_FETCH failed (non-blocking):', profileErr?.message);
        setLoginDebugState(`AUTH_REQUEST_SUCCESS (${authElapsed}ms) → PROFILE_FETCH skipped: ${profileErr?.message}`);
      }

      // Redirect
      console.log('[SignIn] REDIRECT_START');
      navigate('/workshop-dashboard');
      console.log('[SignIn] REDIRECT_OK');

    } catch (err) {
      console.error('[SignIn] AUTH_REQUEST_ERROR unexpected:', err?.message);
      setLoginDebugState(`AUTH_REQUEST_ERROR: ${err?.message || 'unexpected'}`);
      setError(err?.message || 'Error inesperado al iniciar sesión.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAccess = async (account) => {
    setError('');
    setLoading(true);
    setEmail(account?.email);
    setPassword(account?.password);
    try {
      const { error: signInError } = await signIn(account?.email, account?.password);
      if (signInError) {
        setError(signInError?.message || 'Error al iniciar sesión');
      } else {
        navigate('/workshop-dashboard');
      }
    } catch (err) {
      setError('Error al iniciar sesión. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // ── Portal cliente login ──────────────────────────────────────────────────

  const handlePortalLogin = async (e) => {
    e?.preventDefault();
    setPortalError('');
    setPortalLoading(true);
    console.log('LOGIN_START');

    try {
      // HOTFIX: solo signInWithPassword, sin fetches extra
      const { data: authData, error: authError } = await supabase?.auth?.signInWithPassword({
        email: portalEmail,
        password: portalPassword,
      });

      if (authError) {
        console.log('LOGIN_AUTH_ERROR', authError?.message);
        setPortalError('Correo o contraseña incorrectos. Intenta de nuevo.');
        return;
      }

      const userId = authData?.user?.id;
      const userEmail = authData?.user?.email;

      if (!userId) {
        console.log('LOGIN_AUTH_ERROR', 'No userId');
        setPortalError('No se pudo verificar tu identidad. Intenta de nuevo.');
        return;
      }

      console.log('LOGIN_AUTH_OK');

      // Handle "Recordar mis datos"
      if (rememberData) {
        localStorage.setItem(PORTAL_REMEMBERED_EMAIL_KEY, portalEmail?.trim());
      } else {
        localStorage.removeItem(PORTAL_REMEMBERED_EMAIL_KEY);
      }

      // Handle "Recordar sesión" — if NOT checked, mark as session-only
      if (!rememberSession) {
        sessionStorage.setItem(PORTAL_SESSION_ONLY_KEY, '1');
      } else {
        sessionStorage.removeItem(PORTAL_SESSION_ONLY_KEY);
      }

      // HOTFIX: entrar al portal inmediatamente sin esperar perfil/cliente/rol
      setPortalUser({ authUserId: userId, authEmail: userEmail, _loading: true });
      console.log('LOGIN_REDIRECT');

      // HOTFIX: background lookups comentados temporalmente para evitar bloqueo
      // (async () => {
      //   try {
      //     let client = null;
      //     const { data: clientByUserId, error: clientByUserIdError } = await supabase
      //       ?.from('clients')
      //       ?.select('id, nombre, email, telefono, company_id, portal_user_id')
      //       ?.eq('portal_user_id', userId)
      //       ?.maybeSingle();
      //     if (!clientByUserIdError && clientByUserId?.id) { client = clientByUserId; }
      //     if (!client && userEmail) {
      //       const normalizedEmail = userEmail?.trim()?.toLowerCase();
      //       const { data: clientByEmail, error: clientByEmailError } = await supabase
      //         ?.from('clients')
      //         ?.select('id, nombre, email, telefono, company_id, portal_user_id')
      //         ?.ilike('email', normalizedEmail)
      //         ?.maybeSingle();
      //       if (!clientByEmailError && clientByEmail?.id) { client = clientByEmail; }
      //     }
      //     if (!client) {
      //       setPortalUser({ authUserId: userId, authEmail: userEmail, _needsRegistration: true });
      //       return;
      //     }
      //     if (!client?.company_id) {
      //       await supabase?.auth?.signOut();
      //       setPortalUser(null);
      //       setPortalError('Tu cuenta de cliente no tiene empresa asignada. Contacta al taller.');
      //       return;
      //     }
      //     let accessReq = null;
      //     try {
      //       const { data } = await supabase
      //         ?.from('portal_access_requests')
      //         ?.select('requiere_cambio_password, password_temporal_activa')
      //         ?.eq('matched_cliente_id', client?.id)
      //         ?.order('created_at', { ascending: false })
      //         ?.limit(1)
      //         ?.maybeSingle();
      //       accessReq = data;
      //     } catch (_) {}
      //     setPortalUser({
      //       ...client,
      //       password_temporal_activa: accessReq?.password_temporal_activa === true,
      //       requiere_cambio_password: accessReq?.requiere_cambio_password === true,
      //     });
      //   } catch (bgErr) {
      //     console.error('[SignIn] background client lookup error:', bgErr);
      //   }
      // })();
    } catch (err) {
      console.log('LOGIN_AUTH_ERROR', err?.message);
      setPortalError('Error al iniciar sesión. Por favor intenta de nuevo.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handlePortalLogout = async () => {
    try {
      await supabase?.auth?.signOut();
    } catch (err) {
      console.error('[SignIn] portal signOut error:', err);
    }
    setPortalUser(null);
    setPortalEmail('');
    setPortalPassword('');
    setPortalError('');
    setShowPortalLogin(false);
    sessionStorage.removeItem(PORTAL_SESSION_ONLY_KEY);
  };

  // ── Solicitar acceso handler ──────────────────────────────────────────────

  const handleRequestAccess = async (e) => {
    e?.preventDefault();

    // Prevent double submit
    if (requestLoading) return;

    // Client-side validation — only name is required
    const nombreTrimmed = requestNombre?.trim();
    const telefonoTrimmed = requestTelefono?.trim();
    const emailTrimmed = requestEmail?.trim();

    if (!nombreTrimmed) {
      setRequestError('El nombre completo es requerido.');
      return;
    }

    setRequestError('');
    setRequestLoading(true);

    try {
      const result = await submitPortalAccessRequest({
        nombre: nombreTrimmed,
        telefono: telefonoTrimmed,
        email: emailTrimmed,
      });

      if (!result?.success) {
        setRequestError(result?.error || 'No se pudo enviar la solicitud. Intenta de nuevo.');
        return;
      }

      setRequestResult({ existingClient: result?.existingClient, requestId: result?.requestId });
    } catch (err) {
      console.error('[SignIn] handleRequestAccess unexpected error:', err);
      setRequestError(err?.message || 'Error inesperado. Por favor intenta de nuevo.');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleBackFromRequest = () => {
    setShowRequestAccess(false);
    setRequestNombre('');
    setRequestTelefono('');
    setRequestEmail('');
    setRequestError('');
    setRequestResult(null);
  };

  // ── If portal user is authenticated → render embedded portal ─────────────

  if (portalUser) {
    return <PortalClienteEmbedded initialPortalUser={portalUser} onLogout={handlePortalLogout} />;
  }

  // ── Main sign-in page ─────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
              <Icon name="Wrench" className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">GearControl</h1>
            <p className="text-gray-600">Sistema de Gestión de Taller</p>
          </div>

          {/* ── Solicitar Acceso Panel ── */}
          {showRequestAccess ? (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <button
                  type="button"
                  onClick={handleBackFromRequest}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
                >
                  <Icon name="ArrowLeft" className="w-4 h-4" />
                  Volver
                </button>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-50 rounded-xl mb-3">
                  <Icon name="UserPlus" className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Solicitar acceso</h2>
                <p className="text-gray-500 text-sm mt-1">Portal Cliente</p>
              </div>

              {requestResult ? (
                /* ── Success state ── */
                (<div className="text-center space-y-4">
                  {requestResult?.existingClient ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                      <div className="flex justify-center mb-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Icon name="CheckCircle" className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                      <p className="text-blue-900 font-semibold text-sm">¡Solicitud enviada!</p>
                      <p className="text-blue-700 text-sm mt-1">
                        Encontramos tu registro como cliente. El equipo del taller activará tu acceso al portal en breve.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                      <div className="flex justify-center mb-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <Icon name="Clock" className="w-6 h-6 text-amber-600" />
                        </div>
                      </div>
                      <p className="text-amber-900 font-semibold text-sm">Solicitud pendiente de revisión</p>
                      <p className="text-amber-700 text-sm mt-1">
                        Tu solicitud fue registrada. La administración la revisará y te contactará para confirmar tu acceso.
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleBackFromRequest}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Volver al inicio
                  </button>
                </div>)
              ) : (
                /* ── Request form ── */
                (<form onSubmit={handleRequestAccess} className="space-y-4">
                  {requestError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{requestError}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Nombre completo <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      value={requestNombre}
                      onChange={(e) => setRequestNombre(e?.target?.value)}
                      placeholder="Tu nombre"
                      disabled={requestLoading}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Teléfono</label>
                    <Input
                      type="tel"
                      value={requestTelefono}
                      onChange={(e) => setRequestTelefono(e?.target?.value)}
                      placeholder="10 dígitos"
                      disabled={requestLoading}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Correo electrónico</label>
                    <Input
                      type="email"
                      value={requestEmail}
                      onChange={(e) => setRequestEmail(e?.target?.value)}
                      placeholder="tu@correo.com"
                      disabled={requestLoading}
                      className="w-full"
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    * Nombre requerido. Teléfono o correo opcionales para que podamos contactarte.
                  </p>
                  <Button
                    type="submit"
                    disabled={requestLoading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg transition-colors"
                  >
                    {requestLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Icon name="Loader2" className="w-5 h-5 animate-spin" />
                        Enviando solicitud...
                      </span>
                    ) : (
                      'Enviar solicitud de acceso'
                    )}
                  </Button>
                </form>)
              )}
            </div>

          ) : showPortalLogin ? (
            /* ── Portal Login Panel ── */
            (<div>
              <div className="flex items-center gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => { setShowPortalLogin(false); setPortalError(''); setPortalPassword(''); }}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
                >
                  <Icon name="ArrowLeft" className="w-4 h-4" />
                  Volver
                </button>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-xl mb-3">
                  <Icon name="User" className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Portal Cliente</h2>
                <p className="text-gray-500 text-sm mt-1">Ingresa con tu cuenta de cliente</p>
              </div>
              <form onSubmit={handlePortalLogin} className="space-y-5">
                {portalError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{portalError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electrónico</label>
                  <Input
                    type="email"
                    value={portalEmail}
                    onChange={(e) => setPortalEmail(e?.target?.value)}
                    placeholder="tu@correo.com"
                    required
                    disabled={portalLoading}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                  <div className="relative">
                    <Input
                      type={showPortalPassword ? 'text' : 'password'}
                      value={portalPassword}
                      onChange={(e) => setPortalPassword(e?.target?.value)}
                      placeholder="••••••••"
                      required
                      disabled={portalLoading}
                      className="w-full pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPortalPassword(!showPortalPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      disabled={portalLoading}
                    >
                      <Icon name={showPortalPassword ? 'EyeOff' : 'Eye'} className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Checkboxes — solo portal cliente */}
                <div className="space-y-2.5 pt-1">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={rememberSession}
                      onChange={(e) => setRememberSession(e?.target?.checked)}
                      disabled={portalLoading}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Recordar sesión</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={rememberData}
                      onChange={(e) => setRememberData(e?.target?.checked)}
                      disabled={portalLoading}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Recordar mis datos</span>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={portalLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors"
                >
                  {portalLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="Loader2" className="w-5 h-5 animate-spin" />
                      Iniciando sesión...
                    </span>
                  ) : (
                    'Iniciar sesión como cliente'
                  )}
                </Button>
              </form>
              {/* Solicitar acceso link */}
              <div className="mt-5 text-center">
                <p className="text-sm text-gray-500">
                  ¿Aún no tienes acceso?{' '}
                  <button
                    type="button"
                    onClick={() => { setPortalError(''); setShowPortalLogin(false); setShowRequestAccess(true); }}
                    className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2 transition-colors"
                  >
                    Solicitar acceso
                  </button>
                </p>
              </div>
            </div>)
          ) : (
            <>
              {/* ── Internal Staff Login Form ── */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Correo Electrónico
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    placeholder="usuario@ejemplo.com"
                    required
                    disabled={loading}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Contraseña
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e?.target?.value)}
                      placeholder="••••••••"
                      required
                      disabled={loading}
                      className="w-full pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      disabled={loading}
                    >
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="Loader2" className="w-5 h-5 animate-spin" />
                      Iniciando sesión...
                    </span>
                  ) : (
                    'Iniciar Sesión'
                  )}
                </Button>

                {/* DEBUG: estado del login — solo visible en /signin */}
                {loginDebugState ? (
                  <p className="text-xs font-mono text-center mt-2 px-2 py-1 rounded bg-gray-100 text-gray-600 border border-gray-200">
                    🔍 {loginDebugState}
                  </p>
                ) : null}

              </form>

              {/* Acceso rápido */}
              <div className="mt-6">
                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Acceso rápido</span>
                  </div>
                </div>
                <p className="text-xs text-center text-gray-400 mb-3">Cuentas de prueba — solo uso interno</p>
                <div className="grid grid-cols-2 gap-2">
                  {TEST_ACCOUNTS?.map((account) => (
                    <button
                      key={account?.label}
                      type="button"
                      onClick={() => handleQuickAccess(account)}
                      disabled={loading}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${account?.color} disabled:opacity-50`}
                    >
                      {account?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Portal Cliente access */}
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">¿Eres cliente?</span>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <button
                    type="button"
                    onClick={() => { setShowPortalLogin(true); setPortalError(''); }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-blue-200 rounded-lg text-sm font-semibold text-blue-700 bg-white hover:bg-blue-50 hover:border-blue-400 transition-colors"
                  >
                    <Icon name="User" className="w-4 h-4" />
                    Acceder al Portal Cliente
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowRequestAccess(true); setRequestError(''); setRequestResult(null); }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    <Icon name="UserPlus" className="w-4 h-4" />
                    Solicitar acceso
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-sm text-gray-600 mt-6">
          ¿Necesitas ayuda?{' '}
          <Link to="/contact" className="text-blue-600 hover:text-blue-700 font-medium">
            Contacta soporte
          </Link>
        </p>
      </div>
    </div>
  );
};

// ── Safe fallback login form — shown if SignIn crashes ────────────────────────
const SignInFallback = ({ error: fallbackError, ...props }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email?.trim() || !password) { setErr('Ingresa correo y contraseña.'); return; }
    setErr('');
    setLoading(true);
    try {
      const { supabase: sb } = await import('../../lib/supabase');
      const { error: authErr } = await sb?.auth?.signInWithPassword({ email: email?.trim(), password });
      if (authErr) { setErr('Correo o contraseña incorrectos.'); return; }
      window.location.href = '/workshop-dashboard';
    } catch (e) {
      setErr('Error al iniciar sesión. Recarga la página e intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c-.94 1.543-.826 3.31 2.37 2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">GearControl</h1>
            <p className="text-gray-600">Sistema de Gestión de Taller</p>
          </div>
          {err && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{err}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electrónico</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e?.target?.value)}
                placeholder="usuario@ejemplo.com"
                disabled={loading}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e?.target?.value)}
                placeholder="••••••••"
                disabled={loading}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-60"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

class SignInErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state?.hasError) {
      return <SignInFallback />;
    }
    return this.props?.children;
  }
}

const SignInWithBoundary = () => (
  <SignInErrorBoundary>
    <SignIn />
  </SignInErrorBoundary>
);

export default SignInWithBoundary;